import { FC, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

import Text from '@/components/Text';
import { MLProjectTranslation } from '@/types/resources';
import { MLProjectTranslationStatus } from '@/utils/enums';

import Card from './Card';

const Row = styled.div`
  display: flex;
  align-items: center;
  height: 38px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  padding: 8px 16px;

  &:first-of-type {
    margin-bottom: 12px;
  }
`;

const LeftColumn = styled.div`
  flex-shrink: 0;
  width: 112px;
  display: flex;
  align-items: center;
  border-right: 1px solid ${({ theme }) => theme.colors.lightGray};
  svg {
    margin-right: 8px;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  overflow-x: auto;
`;

const Language = styled(Text)`
  display: inline-flex;
  padding: 0 16px;
  border-right: 1px solid ${({ theme }) => theme.colors.lightGray};
  &:last-of-type {
    border: 0;
  }
`;

interface Props {
  projectId: number;
  translations: MLProjectTranslation[];
}

const ResultsTrackingCard: FC<Props> = ({ projectId, translations }) => {
  const theme = useTheme();

  const doneTranslations = useMemo(() => {
    return translations.filter(
      (t) => t.status === MLProjectTranslationStatus.DONE,
    );
  }, [translations]);

  const pendingTranslations = useMemo(() => {
    return translations.filter(
      (t) => t.status === MLProjectTranslationStatus.PENDING,
    );
  }, [translations]);

  return (
    <Card
      storageKey={`ml_project_${projectId}_results_tracking_card_visibility`}
      title="Results tracking status"
      defaultIsOpened
      description="Track the status of languages that are completed and pending"
    >
      <Row>
        <LeftColumn>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z"
              fill={theme.colors.success}
            />
            <path
              d="M9.96214 5.38464L6.0134 9.15387L4.03906 7.26926"
              stroke={theme.colors.white}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Text as="span" $type="smallBold" $color="gray">
            Done:&nbsp;
          </Text>
          <Text as="span" $type="smallBold">
            {doneTranslations.length}
          </Text>
        </LeftColumn>
        <RightColumn>
          {doneTranslations.map((t) => (
            <Language key={t.id} as="span" $type="smallBold">
              {t.language}
            </Language>
          ))}
        </RightColumn>
      </Row>
      <Row>
        <LeftColumn>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_10475_229230)">
              <path
                d="M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z"
                fill={theme.colors.warning}
              />
              <path
                d="M7 3.5V7.26923H10.7692"
                stroke={theme.colors.white}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_10475_229230">
                <rect width="14" height="14" fill={theme.colors.white} />
              </clipPath>
            </defs>
          </svg>
          <Text as="span" $type="smallBold" $color="gray">
            Pending:&nbsp;
          </Text>
          <Text as="span" $type="smallBold">
            {pendingTranslations.length}
          </Text>
        </LeftColumn>
        <RightColumn>
          {pendingTranslations.map((t) => (
            <Language key={t.id} as="span" $type="smallBold">
              {t.language}
            </Language>
          ))}
        </RightColumn>
      </Row>
    </Card>
  );
};

export default ResultsTrackingCard;
